import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  ArrowUpDown,
  AlertTriangle,
  CheckCircle,
  Trash2,
  RefreshCw,
  Shield,
  Loader2,
  BookOpen,
  MessageSquare,
  BarChart,
  XCircle
} from "lucide-react";

export default function AdminAudit() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Deletion flow state
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [deletionStep, setDeletionStep] = useState(1);
  const [keepEmails, setKeepEmails] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deletionResult, setDeletionResult] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit/users`, {
        withCredentials: true
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error("Failed to load user audit");
      console.error("Audit fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const sortUsers = (userList) => {
    if (!userList) return [];
    return [...userList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle strings
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = sortDirection === "desc" ? "" : "zzz";
      if (bVal === null || bVal === undefined) bVal = sortDirection === "desc" ? "" : "zzz";
      
      if (sortDirection === "desc") {
        return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
      }
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => toggleSort(field)}
      className={`flex items-center gap-1 text-xs font-medium hover:text-[#a69373] transition-colors ${
        sortField === field ? "text-[#a69373]" : "text-white/60"
      }`}
    >
      {children}
      <ArrowUpDown className="w-3 h-3" />
    </button>
  );

  const handlePreviewDeletion = async () => {
    const emails = keepEmails
      .split('\n')
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    if (emails.length === 0) {
      toast.error("Please enter at least one email to keep");
      return;
    }
    
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/admin/audit/preview-deletion`, {
        keep_emails: emails
      }, { withCredentials: true });
      
      if (!res.data.success) {
        toast.error(res.data.error);
        return;
      }
      
      setDeletionPreview(res.data);
      setDeletionStep(2);
      
      if (res.data.keep_emails_not_found?.length > 0) {
        toast.warning(`Warning: ${res.data.keep_emails_not_found.length} email(s) not found in database`);
      }
    } catch (error) {
      toast.error("Failed to preview deletion");
      console.error(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleExecuteDeletion = async () => {
    const emails = keepEmails
      .split('\n')
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    const confirmNum = parseInt(confirmationInput, 10);
    
    if (confirmNum !== deletionPreview?.accounts_to_delete) {
      toast.error(`Confirmation mismatch. Enter ${deletionPreview?.accounts_to_delete} to confirm.`);
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/audit/execute-deletion`, {
        keep_emails: emails,
        confirmation_count: confirmNum
      }, { withCredentials: true });
      
      if (!res.data.success) {
        toast.error(res.data.error);
        return;
      }
      
      setDeletionResult(res.data);
      setDeletionStep(3);
      toast.success(`Successfully deleted ${res.data.accounts_deleted} accounts`);
      
      // Refresh the user list
      fetchUsers();
    } catch (error) {
      toast.error("Deletion failed");
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const resetDeletionFlow = () => {
    setShowDeletionDialog(false);
    setDeletionStep(1);
    setKeepEmails("");
    setDeletionPreview(null);
    setConfirmationInput("");
    setDeletionResult(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="h-64 bg-muted rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <section className="mb-8 animate-fade-in flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#a69373' }}>
              <Users className="w-8 h-8 inline mr-2" />
              User Audit
            </h1>
            <p className="text-[#a69373]/70">
              {users.length} total accounts
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={fetchUsers}
              variant="outline"
              className="border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowDeletionDialog(true)}
              variant="outline"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Bulk Delete
            </Button>
          </div>
        </section>

        {/* User Table */}
        <section>
          <Card className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left p-3">
                      <SortButton field="email">Email</SortButton>
                    </th>
                    <th className="text-left p-3">
                      <SortButton field="name">Name</SortButton>
                    </th>
                    <th className="text-center p-3">
                      <SortButton field="subscription_type">Tier</SortButton>
                    </th>
                    <th className="text-center p-3">
                      <SortButton field="created_at">Registered</SortButton>
                    </th>
                    <th className="text-center p-3">
                      <SortButton field="journal_entries">Journals</SortButton>
                    </th>
                    <th className="text-center p-3">
                      <SortButton field="check_ins">Check-ins</SortButton>
                    </th>
                    <th className="text-center p-3">
                      <SortButton field="pathfinder_messages">PathFinder</SortButton>
                    </th>
                    <th className="text-center p-3 text-xs font-medium text-white/60">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortUsers(users).map((user, idx) => (
                    <tr 
                      key={user.user_id} 
                      className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                        idx % 2 === 0 ? "bg-transparent" : "bg-white/[0.02]"
                      } ${user.is_protected ? "bg-amber-900/10" : ""}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {user.is_protected && <Shield className="w-4 h-4 text-amber-400" />}
                          <span className="text-sm text-white">{user.email}</span>
                        </div>
                      </td>
                      <td className="p-3 text-sm text-white/80">{user.name || "—"}</td>
                      <td className="p-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          user.subscription_type === "annual" ? "bg-amber-500/20 text-amber-400" :
                          user.subscription_type === "quarterly" ? "bg-purple-500/20 text-purple-400" :
                          user.subscription_type === "monthly" ? "bg-blue-500/20 text-blue-400" :
                          "bg-white/10 text-white/50"
                        }`}>
                          {user.subscription_type || "free"}
                        </span>
                      </td>
                      <td className="p-3 text-center text-sm text-white/60">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`text-sm ${user.journal_entries > 0 ? "text-green-400" : "text-white/30"}`}>
                          {user.journal_entries}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`text-sm ${user.check_ins > 0 ? "text-blue-400" : "text-white/30"}`}>
                          {user.check_ins}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`text-sm ${user.pathfinder_messages > 0 ? "text-purple-400" : "text-white/30"}`}>
                          {user.pathfinder_messages}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        {user.is_admin ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">Admin</span>
                        ) : user.is_protected ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">Protected</span>
                        ) : (
                          <span className="text-xs text-white/30">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        {/* Deletion Dialog */}
        <Dialog open={showDeletionDialog} onOpenChange={(open) => !open && resetDeletionFlow()}>
          <DialogContent className="bg-[#0B2821] border border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2 text-red-400">
                <Trash2 className="w-5 h-5" />
                Bulk Account Deletion
              </DialogTitle>
              <DialogDescription className="text-white/60">
                {deletionStep === 1 && "Step 1: Enter emails to KEEP"}
                {deletionStep === 2 && "Step 2: Review and confirm deletion"}
                {deletionStep === 3 && "Deletion complete"}
              </DialogDescription>
            </DialogHeader>
            
            {/* Step 1: Enter keep list */}
            {deletionStep === 1 && (
              <div className="space-y-4 py-2">
                <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/20">
                  <p className="text-sm text-amber-200 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <strong>Warning:</strong> All accounts NOT in this list will be permanently deleted.
                  </p>
                </div>
                
                <div className="p-3 rounded-lg bg-blue-900/20 border border-blue-500/20">
                  <p className="text-sm text-blue-200 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <strong>info@truenorth-zone.com</strong> is always protected and cannot be deleted.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#a69373]">Emails to KEEP (one per line)</Label>
                  <Textarea
                    value={keepEmails}
                    onChange={(e) => setKeepEmails(e.target.value)}
                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                    className="min-h-[150px] bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                  <p className="text-xs text-white/50">
                    {keepEmails.split('\n').filter(e => e.trim() && e.includes('@')).length} email(s) entered
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-2">
                  <Button 
                    variant="ghost" 
                    onClick={resetDeletionFlow}
                    className="text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={handlePreviewDeletion}
                    disabled={previewLoading || !keepEmails.trim()}
                  >
                    {previewLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Previewing...
                      </>
                    ) : (
                      "Preview Deletion"
                    )}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 2: Review and confirm */}
            {deletionStep === 2 && deletionPreview && (
              <div className="space-y-4 py-2">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-green-900/20 border border-green-500/20">
                    <p className="text-sm text-green-400 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Keeping: {deletionPreview.accounts_to_keep} account(s)
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/20">
                    <p className="text-sm text-red-400 flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Deleting: {deletionPreview.accounts_to_delete} account(s)
                    </p>
                  </div>
                </div>
                
                {/* Not found warning */}
                {deletionPreview.keep_emails_not_found?.length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-900/20 border border-amber-500/20">
                    <p className="text-sm text-amber-200 mb-2">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      These emails from your keep list were NOT found:
                    </p>
                    <ul className="text-xs text-amber-300/80 ml-6 list-disc">
                      {deletionPreview.keep_emails_not_found.map(e => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Accounts to delete */}
                <div className="space-y-2">
                  <Label className="text-red-400">Accounts that will be DELETED:</Label>
                  <div className="max-h-[200px] overflow-y-auto rounded-lg border border-white/10 bg-white/5">
                    <table className="w-full text-sm">
                      <thead className="bg-white/5 sticky top-0">
                        <tr>
                          <th className="text-left p-2 text-xs text-white/60">Email</th>
                          <th className="text-center p-2 text-xs text-white/60">Journals</th>
                          <th className="text-center p-2 text-xs text-white/60">Check-ins</th>
                          <th className="text-center p-2 text-xs text-white/60">PF Msgs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deletionPreview.users_to_delete.map(u => (
                          <tr key={u.user_id} className="border-t border-white/5">
                            <td className="p-2 text-white/80">{u.email}</td>
                            <td className="p-2 text-center text-white/60">{u.journal_entries}</td>
                            <td className="p-2 text-center text-white/60">{u.check_ins}</td>
                            <td className="p-2 text-center text-white/60">{u.pathfinder_messages}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {/* Confirmation input */}
                <div className="p-4 rounded-lg bg-red-900/30 border border-red-500/30 space-y-3">
                  <p className="text-sm text-red-300">
                    To confirm deletion, type the number <strong className="text-white">{deletionPreview.accounts_to_delete}</strong> below:
                  </p>
                  <Input
                    type="number"
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.target.value)}
                    placeholder={`Type ${deletionPreview.accounts_to_delete} to confirm`}
                    className="bg-white/10 border-red-500/30 text-white text-center text-lg"
                  />
                </div>
                
                <div className="flex justify-between gap-3 pt-2">
                  <Button 
                    variant="ghost" 
                    onClick={() => setDeletionStep(1)}
                    className="text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <div className="flex gap-3">
                    <Button 
                      variant="ghost" 
                      onClick={resetDeletionFlow}
                      className="text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={handleExecuteDeletion}
                      disabled={deleting || parseInt(confirmationInput, 10) !== deletionPreview.accounts_to_delete}
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete {deletionPreview.accounts_to_delete} Accounts
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Completion */}
            {deletionStep === 3 && deletionResult && (
              <div className="space-y-4 py-2">
                <div className="p-6 rounded-lg bg-green-900/20 border border-green-500/20 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <h3 className="text-lg font-semibold text-white mb-2">Deletion Complete</h3>
                  <p className="text-green-300">
                    Successfully deleted {deletionResult.accounts_deleted} account(s) and all associated data.
                  </p>
                  <p className="text-xs text-white/50 mt-2">
                    Deleted at: {new Date(deletionResult.deleted_at).toLocaleString()}
                  </p>
                </div>
                
                <div className="flex justify-center pt-2">
                  <Button 
                    className="bg-[#a69373] hover:bg-[#b89a5e] text-[#0B2821]"
                    onClick={resetDeletionFlow}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
